import React, { useCallback, useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, Edit, X, Download, TrendingUp, TrendingDown, Target, Award, AlertCircle, Calendar, BarChart3, RefreshCw, ListOrdered, Sparkles, Search } from "lucide-react";

const EMOTIONS = ["calm","confident","patient","greedy","fearful","frustrated","revenge","impulsive"];

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ symbol: "", tag: "", outcome: "" });
  const [editing, setEditing] = useState(null); // JournalEntry or "new"
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | calendar | report
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.symbol) params.symbol = filter.symbol.toUpperCase();
      if (filter.tag) params.tag = filter.tag;
      if (filter.outcome) params.outcome = filter.outcome;
      const [e, s] = await Promise.all([
        api.get("/journal", { params }),
        api.get("/journal/stats"),
      ]);
      setEntries(e.data);
      setStats(s.data);
    } catch { toast.error("Failed to load journal"); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const del = async (jid) => {
    if (!window.confirm("Delete this journal entry?")) return;
    await api.delete(`/journal/${jid}`);
    toast.success("Deleted");
    load();
  };

  const exportCsv = () => {
    const token = getToken();
    const url = `${API}/journal-export.csv`;
    // Use a hidden link with credentials via a fetch → blob (cookies flow)
    (async () => {
      try {
        const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new Error("CSV export failed");
        const blob = await res.blob();
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href; a.download = "mql4forge-journal.csv"; a.click();
        URL.revokeObjectURL(href);
        toast.success("Exported journal.csv");
      } catch (e) { toast.error(e.message || "Export failed"); }
    })();
  };

  const syncBroker = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/journal-sync-broker");
      if (data.imported > 0) toast.success(`Imported ${data.imported} closed trade${data.imported === 1 ? "" : "s"}`);
      else toast.info(data.connections_scanned === 0 ? "No broker connections to sync" : "No new closed trades since last sync");
      if ((data.connection_errors || []).length) toast.warning(`${data.connection_errors.length} connection(s) had errors`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  return (
    <div className="min-h-screen"><Navbar/>
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="chip mb-3" data-testid="journal-eyebrow"><BookOpen size={12}/> TRADING JOURNAL</div>
            <h1 className="font-display text-4xl text-white tracking-tight">Your journal</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
              Log every trade, tag setups, capture your emotional state — turn hindsight into edge.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="journal-sync-btn" onClick={syncBroker} disabled={syncing}
              className="btn-outline flex items-center gap-2 text-xs">
              <RefreshCw size={13} className={syncing ? "animate-spin" : ""}/> {syncing ? "Syncing…" : "Sync from broker"}
            </button>
            <button data-testid="journal-export-btn" onClick={exportCsv}
              className="btn-outline flex items-center gap-2 text-xs">
              <Download size={13}/> CSV
            </button>
            <button data-testid="journal-new-btn" onClick={()=>setEditing("new")} className="btn-primary flex items-center gap-2">
              <Plus size={14}/> New entry
            </button>
          </div>
        </div>

        {stats && stats.total > 0 && <StatsPanel stats={stats}/>}

        <div className="flex items-center gap-1 border border-white/10 rounded p-0.5 mb-4 w-fit" data-testid="journal-view-tabs">
          <button data-testid="journal-tab-list" onClick={()=>setView("list")}
            className={`px-3 py-1.5 text-xs font-mono rounded flex items-center gap-1.5 transition-colors ${view==="list" ? "bg-[var(--accent)] text-black" : "text-[var(--text-secondary)] hover:text-white"}`}>
            <ListOrdered size={13}/> List
          </button>
          <button data-testid="journal-tab-calendar" onClick={()=>setView("calendar")}
            className={`px-3 py-1.5 text-xs font-mono rounded flex items-center gap-1.5 transition-colors ${view==="calendar" ? "bg-[var(--accent)] text-black" : "text-[var(--text-secondary)] hover:text-white"}`}>
            <Calendar size={13}/> Calendar
          </button>
          <button data-testid="journal-tab-report" onClick={()=>setView("report")}
            className={`px-3 py-1.5 text-xs font-mono rounded flex items-center gap-1.5 transition-colors ${view==="report" ? "bg-[var(--accent)] text-black" : "text-[var(--text-secondary)] hover:text-white"}`}>
            <BarChart3 size={13}/> Report
          </button>
        </div>

        {view === "calendar" && <JournalCalendar/>}
        {view === "report" && <JournalReport/>}
        {view === "list" && (<>

        <NLJournalSearchBar setEntries={setEntries}/>

        <div className="card-sharp p-4 flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="label-tiny">Symbol</label>
            <input data-testid="journal-filter-symbol" value={filter.symbol}
              onChange={(e)=>setFilter({...filter, symbol: e.target.value})}
              className="input-dark w-32" placeholder="EURUSD"/>
          </div>
          <div>
            <label className="label-tiny">Tag</label>
            <input data-testid="journal-filter-tag" value={filter.tag}
              onChange={(e)=>setFilter({...filter, tag: e.target.value})}
              className="input-dark w-32" placeholder="breakout"/>
          </div>
          <div>
            <label className="label-tiny">Outcome</label>
            <select data-testid="journal-filter-outcome" value={filter.outcome} onChange={(e)=>setFilter({...filter, outcome:e.target.value})} className="input-dark w-32">
              <option value="">All</option>
              <option value="win">Wins</option>
              <option value="loss">Losses</option>
              <option value="breakeven">Breakeven</option>
              <option value="open">Open</option>
            </select>
          </div>
          <button data-testid="journal-clear-filters" onClick={()=>setFilter({symbol:"",tag:"",outcome:""})}
            className="btn-outline text-xs !py-1.5 ml-auto">Clear filters</button>
        </div>

        {loading ? (
          <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">Loading…</p>
        ) : entries.length === 0 ? (
          <div className="card-sharp p-12 text-center" data-testid="journal-empty">
            <BookOpen className="mx-auto text-[var(--text-muted)]" size={40}/>
            <h3 className="font-display text-2xl text-white mt-4">No entries yet</h3>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-md mx-auto">
              Start logging your trades. Even bad trades — especially the bad ones. That's where your edge comes from.
            </p>
            <button onClick={()=>setEditing("new")} className="btn-primary mt-6 inline-flex items-center gap-2"><Plus size={14}/> Add first entry</button>
          </div>
        ) : (
          <div className="card-sharp overflow-auto" data-testid="journal-table">
            <table className="w-full text-xs font-mono">
              <thead className="text-[var(--text-muted)] uppercase tracking-widest sticky top-0 bg-[#0B0D12]">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left">Symbol</th>
                  <th>Side</th>
                  <th className="text-right">Entry</th>
                  <th className="text-right">Exit</th>
                  <th className="text-right">Pips</th>
                  <th className="text-right">USD</th>
                  <th>Strategy</th>
                  <th>Tags</th>
                  <th>Emotion</th>
                  <th className="px-3"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.journal_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`journal-row-${e.journal_id}`}>
                    <td className="px-3 py-2 text-white">{e.date}</td>
                    <td className="text-white">{e.symbol}</td>
                    <td className={e.side==="buy" ? "text-[var(--profit)]" : "text-[var(--loss)]"}>{(e.side||"").toUpperCase()}</td>
                    <td className="text-right">{e.entry_price}</td>
                    <td className="text-right">{e.exit_price ?? "—"}</td>
                    <td className={`text-right ${(e.pnl_pips ?? 0) > 0 ? "text-[var(--profit)]" : (e.pnl_pips ?? 0) < 0 ? "text-[var(--loss)]" : "text-[var(--text-secondary)]"}`}>
                      {e.pnl_pips != null ? (e.pnl_pips > 0 ? "+" : "") + e.pnl_pips : "—"}
                    </td>
                    <td className={`text-right ${(e.pnl_usd ?? 0) > 0 ? "text-[var(--profit)]" : (e.pnl_usd ?? 0) < 0 ? "text-[var(--loss)]" : "text-[var(--text-secondary)]"}`}>
                      {e.pnl_usd != null ? (e.pnl_usd > 0 ? "+$" : "-$") + Math.abs(e.pnl_usd).toFixed(2) : "—"}
                    </td>
                    <td className="text-[var(--text-secondary)]">{e.strategy_name || "—"}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(e.tags || []).slice(0, 3).map((t)=>(<span key={t} className="chip">{t}</span>))}
                      </div>
                    </td>
                    <td className="text-[var(--text-secondary)] italic">{e.emotion || "—"}</td>
                    <td className="px-3">
                      <div className="flex gap-1">
                        <button data-testid={`journal-edit-${e.journal_id}`} onClick={()=>setEditing(e)}
                          className="p-1.5 border border-white/10 rounded hover:border-[var(--accent)] hover:text-[var(--accent)]"><Edit size={12}/></button>
                        <button data-testid={`journal-delete-${e.journal_id}`} onClick={()=>del(e.journal_id)}
                          className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><Trash2 size={12}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>)}

        {editing && (
          <EntryModal
            entry={editing === "new" ? null : editing}
            onClose={()=>setEditing(null)}
            onSaved={()=>{ setEditing(null); load(); }}
          />
        )}
      </main>
    </div>
  );
}

function StatsPanel({ stats }) {
  const pnlPositive = (stats.net_usd || 0) >= 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="journal-stats">
      <BigStat label="Trades" value={stats.total} testId="jstat-total"/>
      <BigStat label="Win rate" value={`${stats.win_rate}%`} testId="jstat-winrate"/>
      <BigStat label="Net P&L"
        value={`${pnlPositive ? "+" : "-"}$${Math.abs(stats.net_usd).toFixed(2)}`}
        highlight={pnlPositive ? "profit" : "loss"} testId="jstat-pnl"/>
      <BigStat label="Avg win" value={`+$${(stats.avg_win_usd ?? 0).toFixed(2)}`} highlight="profit" testId="jstat-avgwin"/>
      <BigStat label="Avg loss" value={`-$${Math.abs(stats.avg_loss_usd ?? 0).toFixed(2)}`} highlight="loss" testId="jstat-avgloss"/>
      <BigStat label="Best trade" value={stats.best_trade ? `+$${(stats.best_trade.pnl_usd ?? 0).toFixed(2)}` : "—"} highlight="profit" testId="jstat-best"/>
    </div>
  );
}

function BigStat({ label, value, highlight, testId }) {
  const color = highlight === "profit" ? "text-[var(--profit)]" : highlight === "loss" ? "text-[var(--loss)]" : "text-white";
  return (
    <div className="card-sharp p-3" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function EntryModal({ entry, onClose, onSaved }) {
  const isEdit = !!entry;
  const [form, setForm] = useState(() => ({
    date: entry?.date || new Date().toISOString().slice(0, 10),
    symbol: entry?.symbol || "EURUSD",
    side: entry?.side || "buy",
    entry_price: entry?.entry_price ?? 0,
    exit_price: entry?.exit_price ?? "",
    lot_size: entry?.lot_size ?? 0.10,
    pnl_usd: entry?.pnl_usd ?? "",
    pnl_pips: entry?.pnl_pips ?? "",
    strategy_name: entry?.strategy_name || "",
    tags: (entry?.tags || []).join(", "),
    notes: entry?.notes || "",
    emotion: entry?.emotion || "",
    mistakes: (entry?.mistakes || []).join(", "),
    lessons: entry?.lessons || "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        symbol: form.symbol.toUpperCase(),
        entry_price: parseFloat(form.entry_price) || 0,
        exit_price: form.exit_price === "" ? null : parseFloat(form.exit_price),
        lot_size: parseFloat(form.lot_size) || 0.10,
        pnl_usd: form.pnl_usd === "" ? null : parseFloat(form.pnl_usd),
        pnl_pips: form.pnl_pips === "" ? null : parseFloat(form.pnl_pips),
        tags: form.tags.split(",").map(t=>t.trim()).filter(Boolean),
        mistakes: form.mistakes.split(",").map(t=>t.trim()).filter(Boolean),
        emotion: form.emotion || null,
      };
      if (isEdit) await api.put(`/journal/${entry.journal_id}`, payload);
      else await api.post(`/journal`, payload);
      toast.success(isEdit ? "Updated" : "Entry added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="journal-modal">
      <div className="card-sharp p-6 max-w-2xl w-full max-h-[92vh] overflow-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <BookOpen size={18} className="text-[var(--accent)]"/>
            <h3 className="font-display text-xl text-white">{isEdit ? "Edit trade" : "New trade"}</h3>
          </div>
          <button data-testid="journal-modal-close" onClick={onClose} className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><X size={14}/></button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Date"><input data-testid="jm-date" type="date" className="input-dark" value={form.date} onChange={(e)=>set("date", e.target.value)}/></Field>
          <Field label="Symbol"><input data-testid="jm-symbol" className="input-dark" value={form.symbol} onChange={(e)=>set("symbol", e.target.value.toUpperCase())}/></Field>
          <Field label="Side">
            <select data-testid="jm-side" className="input-dark" value={form.side} onChange={(e)=>set("side", e.target.value)}>
              <option value="buy">BUY</option><option value="sell">SELL</option>
            </select>
          </Field>
          <Field label="Entry price"><input data-testid="jm-entry" type="number" step="0.00001" className="input-dark" value={form.entry_price} onChange={(e)=>set("entry_price", e.target.value)}/></Field>
          <Field label="Exit price"><input data-testid="jm-exit" type="number" step="0.00001" className="input-dark" value={form.exit_price} onChange={(e)=>set("exit_price", e.target.value)}/></Field>
          <Field label="Lot size"><input data-testid="jm-lot" type="number" step="0.01" className="input-dark" value={form.lot_size} onChange={(e)=>set("lot_size", e.target.value)}/></Field>
          <Field label="P&L USD"><input data-testid="jm-pnl-usd" type="number" step="0.01" className="input-dark" value={form.pnl_usd} onChange={(e)=>set("pnl_usd", e.target.value)}/></Field>
          <Field label="P&L Pips"><input data-testid="jm-pnl-pips" type="number" step="0.1" className="input-dark" value={form.pnl_pips} onChange={(e)=>set("pnl_pips", e.target.value)}/></Field>
          <Field label="Strategy"><input data-testid="jm-strategy" className="input-dark" value={form.strategy_name} onChange={(e)=>set("strategy_name", e.target.value)} placeholder="RSI Reversal"/></Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <Field label="Tags (comma-separated)"><input data-testid="jm-tags" className="input-dark" value={form.tags} onChange={(e)=>set("tags", e.target.value)} placeholder="breakout, morning, london"/></Field>
          <Field label="Emotion">
            <select data-testid="jm-emotion" className="input-dark" value={form.emotion} onChange={(e)=>set("emotion", e.target.value)}>
              <option value="">— none —</option>
              {EMOTIONS.map(em => <option key={em} value={em}>{em}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Mistakes (comma-separated)">
          <input data-testid="jm-mistakes" className="input-dark" value={form.mistakes} onChange={(e)=>set("mistakes", e.target.value)}
            placeholder="chased entry, moved SL, no stop"/>
        </Field>
        <Field label="Notes / lessons">
          <textarea data-testid="jm-lessons" rows={4} className="input-dark !font-sans" value={form.lessons} onChange={(e)=>set("lessons", e.target.value)}
            placeholder="What did I learn? What would I do differently?"/>
        </Field>

        {isEdit && (
          <ScreenshotSection entryId={entry.journal_id}
            initialShots={entry.screenshots || []}/>
        )}
        {!isEdit && (
          <p data-testid="screenshot-save-first" className="text-[11px] font-mono text-[var(--text-muted)] italic mt-4">
            📸 Chart screenshots: save the entry first, then re-open it to attach up to 3 charts.
          </p>
        )}

        <button data-testid="jm-submit" onClick={submit} disabled={busy} className="btn-primary w-full mt-5">
          {busy ? "Saving…" : (isEdit ? "Save changes" : "Add entry")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="mt-3"><label className="label-tiny">{label}</label>{children}</div>;
}


function JournalCalendar() {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  const [days, setDays] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal-calendar", { params: { year, month } });
      setDays(data.days || []);
    } catch { toast.error("Calendar load failed"); }
    finally { setLoading(false); }
  }, [year, month]);
  React.useEffect(() => { load(); }, [load]);

  const dayMap = new Map(days.map(d => [d.date, d]));
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const leadOffset = firstDay.getDay(); // Sun=0
  const cells = [];
  for (let i = 0; i < leadOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, key, data: dayMap.get(key) });
  }

  const monthlyTotal = days.reduce((s, d) => s + (d.pnl_usd || 0), 0);
  const activeDays = days.length;

  const step = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) { m = 1; y += 1; }
    if (m < 1) { m = 12; y -= 1; }
    setMonth(m); setYear(y);
  };
  const monthLabel = firstDay.toLocaleString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="card-sharp p-6" data-testid="journal-calendar">
      <div className="flex items-center gap-4 mb-5 flex-wrap">
        <button data-testid="cal-prev" onClick={()=>step(-1)}
          className="p-2 border border-white/10 rounded hover:border-[var(--accent)] hover:text-[var(--accent)]">←</button>
        <h3 className="font-display text-xl text-white flex-1">{monthLabel}</h3>
        <button data-testid="cal-next" onClick={()=>step(1)}
          className="p-2 border border-white/10 rounded hover:border-[var(--accent)] hover:text-[var(--accent)]">→</button>
        <div className="border border-white/10 rounded px-3 py-1.5 text-xs font-mono" data-testid="cal-total">
          <span className="text-[var(--text-muted)] mr-2">Month P&L</span>
          <span className={monthlyTotal >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}>
            {monthlyTotal >= 0 ? "+" : ""}${monthlyTotal.toFixed(2)}
          </span>
          <span className="text-[var(--text-muted)] ml-3">· {activeDays} active day{activeDays === 1 ? "" : "s"}</span>
        </div>
      </div>

      {loading ? (
        <p className="text-xs font-mono text-[var(--text-muted)]">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((c, i) => c ? <CalendarCell key={c.key} c={c}/> : <div key={`empty-${i}`}/>)}
          </div>
          <div className="mt-4 flex items-center gap-3 text-[10px] font-mono text-[var(--text-muted)]">
            <span>Legend:</span>
            <span className="chip !border-[var(--loss)]/40 !text-[var(--loss)]">Loss day</span>
            <span className="chip">No activity</span>
            <span className="chip !border-[var(--profit)]/40 !text-[var(--profit)]">Profit day</span>
          </div>
        </>
      )}
    </div>
  );
}


function CalendarCell({ c }) {
  const d = c.data;
  const pnl = d?.pnl_usd ?? 0;
  const bg = !d
    ? "bg-black/10 border-white/5"
    : pnl > 0
      ? "bg-[var(--profit)]/10 border-[var(--profit)]/30 hover:!border-[var(--profit)]"
      : pnl < 0
        ? "bg-[var(--loss)]/10 border-[var(--loss)]/30 hover:!border-[var(--loss)]"
        : "bg-white/5 border-white/10";
  return (
    <div className={`aspect-square border rounded p-1 flex flex-col text-[10px] font-mono ${bg} transition-colors`}
      data-testid={`cal-day-${c.key}`}>
      <span className="text-[var(--text-secondary)]">{c.day}</span>
      {d && (
        <div className="mt-auto text-right">
          <div className={pnl >= 0 ? "text-[var(--profit)] text-xs font-semibold" : "text-[var(--loss)] text-xs font-semibold"}>
            {pnl >= 0 ? "+" : ""}${Math.round(pnl)}
          </div>
          <div className="text-[9px] text-[var(--text-muted)]">{d.trades}t</div>
        </div>
      )}
    </div>
  );
}


function JournalReport() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/journal-report/monthly"); setData(data); }
      catch { toast.error("Report load failed"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="text-xs font-mono text-[var(--text-muted)]">Loading report…</p>;
  if (!data || data.months.length === 0) {
    return (
      <div className="card-sharp p-10 text-center" data-testid="report-empty">
        <BarChart3 className="mx-auto text-[var(--text-muted)]" size={40}/>
        <h3 className="font-display text-xl text-white mt-2">Not enough data yet</h3>
        <p className="text-[var(--text-muted)] text-sm">Log a few trades first — reports appear once you have activity.</p>
      </div>
    );
  }

  const maxAbs = Math.max(1, ...data.months.map(m => Math.abs(m.pnl_usd || 0)));
  const adv = data.advanced || {};

  return (
    <div className="space-y-5" data-testid="journal-report">
      {(adv.sharpe_ratio !== undefined) && (
        <div className="card-sharp p-5" data-testid="report-advanced">
          <h3 className="font-display text-lg text-white mb-3">Advanced metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
            <MetricTile testId="metric-sharpe" label="Sharpe ratio (trade)" value={adv.sharpe_ratio}
              hint="Mean/std of per-trade P&L. >1 is decent, >2 is very good."/>
            <MetricTile testId="metric-max-consec-losses" label="Max consec. losses" value={adv.max_consecutive_losses}
              hint="Longest losing streak. Design your risk to survive 2× this."/>
            <MetricTile testId="metric-profit-factor" label="Profit factor" value={adv.profit_factor}
              hint="Sum of wins / sum of losses. >1.5 is healthy."/>
            <MetricTile testId="metric-expectancy" label="Expectancy per trade" value={`$${adv.expectancy_per_trade}`}
              hint="Average $ per trade. Times trades/month = expected P&L."/>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {data.best_day && (
          <div className="card-sharp p-5" data-testid="report-best-day">
            <div className="chip mb-2 !border-[var(--profit)]/40 !text-[var(--profit)]"><TrendingUp size={11}/> BEST DAY</div>
            <div className="font-display text-xl text-white">{data.best_day.date}</div>
            <div className="text-2xl font-display text-[var(--profit)] mt-1">+${data.best_day.pnl_usd.toFixed(2)}</div>
            <div className="text-[11px] font-mono text-[var(--text-muted)]">{data.best_day.trades} trade{data.best_day.trades === 1 ? "" : "s"}</div>
          </div>
        )}
        {data.worst_day && (
          <div className="card-sharp p-5" data-testid="report-worst-day">
            <div className="chip mb-2 !border-[var(--loss)]/40 !text-[var(--loss)]"><TrendingDown size={11}/> WORST DAY</div>
            <div className="font-display text-xl text-white">{data.worst_day.date}</div>
            <div className="text-2xl font-display text-[var(--loss)] mt-1">${data.worst_day.pnl_usd.toFixed(2)}</div>
            <div className="text-[11px] font-mono text-[var(--text-muted)]">{data.worst_day.trades} trade{data.worst_day.trades === 1 ? "" : "s"}</div>
          </div>
        )}
      </div>

      <div className="card-sharp p-5" data-testid="report-monthly-chart">
        <h3 className="font-display text-xl text-white mb-4">Monthly P&L</h3>
        <div className="space-y-2">
          {data.months.map(m => {
            const w = Math.abs(m.pnl_usd) / maxAbs * 100;
            const positive = m.pnl_usd >= 0;
            return (
              <div key={m.month} className="flex items-center gap-2 text-xs font-mono" data-testid={`report-month-${m.month}`}>
                <span className="w-16 text-[var(--text-muted)]">{m.month}</span>
                <div className="flex-1 h-6 relative bg-black/30 rounded overflow-hidden border border-white/5">
                  <div className={`absolute inset-y-0 ${positive ? "left-1/2 bg-[var(--profit)]/70" : "right-1/2 bg-[var(--loss)]/70"}`}
                    style={{ width: `${w / 2}%` }}/>
                  <div className="absolute inset-y-0 left-1/2 w-px bg-white/20"/>
                </div>
                <span className={`w-24 text-right ${positive ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>
                  {positive ? "+" : ""}${m.pnl_usd.toFixed(2)}
                </span>
                <span className="w-20 text-right text-[var(--text-secondary)]">{m.trades}t · {m.win_rate}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-sharp p-5" data-testid="report-symbols">
          <h3 className="font-display text-lg text-white mb-3">P&L by symbol</h3>
          {data.by_symbol.length === 0 ? (
            <p className="text-[11px] font-mono text-[var(--text-muted)] italic">No data.</p>
          ) : (
            <ul className="space-y-2 text-xs font-mono">
              {data.by_symbol.map(s => (
                <li key={s.symbol} className="flex items-center gap-2" data-testid={`report-symbol-${s.symbol}`}>
                  <span className="w-16 text-white">{s.symbol}</span>
                  <span className={`flex-1 text-right ${s.pnl_usd >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>
                    {s.pnl_usd >= 0 ? "+" : ""}${s.pnl_usd.toFixed(2)}
                  </span>
                  <span className="w-24 text-right text-[var(--text-muted)]">{s.trades}t · {s.win_rate}%</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-sharp p-5" data-testid="report-mistakes">
          <h3 className="font-display text-lg text-white mb-3">Most common mistakes</h3>
          {data.top_mistakes.length === 0 ? (
            <p className="text-[11px] font-mono text-[var(--text-muted)] italic">You haven't tagged any mistakes yet — keep logging.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {data.top_mistakes.map(m => (
                <li key={m.mistake} className="chip !border-[var(--warning)]/40 !text-[var(--warning)]"
                  data-testid={`report-mistake-${m.mistake.replace(/\W+/g,"-")}`}>
                  {m.mistake} <span className="text-[var(--text-muted)] ml-1">×{m.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ScreenshotSection({ entryId, initialShots }) {
  const [shots, setShots] = React.useState(initialShots || []);
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);
  const MAX_SIZE = 700_000; // matches backend

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed"); return; }
    if (shots.length >= 3) { toast.error("Max 3 screenshots per entry"); return; }
    // Downscale via canvas to keep the base64 under ~500KB
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const scale = Math.min(1, 1280 / img.width);
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            // Try quality 0.85 first, fall back lower if oversized
            let q = 0.85;
            let out = canvas.toDataURL("image/jpeg", q);
            while (out.length > MAX_SIZE && q > 0.3) { q -= 0.1; out = canvas.toDataURL("image/jpeg", q); }
            resolve(out);
          };
          img.onerror = reject;
          img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post(`/journal/${entryId}/screenshot`, { data_url: dataUrl });
      setShots([...shots, { id: data.screenshot_id, data_url: dataUrl }]);
      toast.success("Screenshot added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async (sid) => {
    try {
      await api.delete(`/journal/${entryId}/screenshot/${sid}`);
      setShots(shots.filter(s => s.id !== sid));
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-4 border-t border-white/5 pt-4" data-testid="screenshot-section">
      <div className="flex items-center justify-between mb-2">
        <label className="label-tiny">Chart screenshots ({shots.length}/3)</label>
        {shots.length < 3 && (
          <button data-testid="jm-add-screenshot" onClick={()=>inputRef.current?.click()}
            disabled={uploading}
            className="btn-outline text-xs !py-1">
            {uploading ? "Uploading…" : "+ Attach chart"}
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
          onChange={(e)=>onFile(e.target.files?.[0])} data-testid="jm-screenshot-input"/>
      </div>
      {shots.length === 0 ? (
        <p className="text-[11px] font-mono text-[var(--text-muted)] italic">No screenshots attached yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {shots.map((s) => (
            <div key={s.id} className="relative border border-white/10 rounded overflow-hidden group"
              data-testid={`screenshot-${s.id}`}>
              <img src={s.data_url} alt="chart" className="w-full h-24 object-cover"/>
              <button data-testid={`screenshot-remove-${s.id}`}
                onClick={()=>remove(s.id)}
                className="absolute top-1 right-1 p-1 bg-black/70 border border-white/20 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:!border-[var(--loss)] hover:!text-[var(--loss)]">
                <X size={11}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricTile({ label, value, hint, testId }) {
  return (
    <div className="border border-white/5 rounded p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
      <div className="font-display text-2xl mt-1 text-white">{value}</div>
      <div className="text-[10px] text-[var(--text-muted)] mt-1">{hint}</div>
    </div>
  );
}

function NLJournalSearchBar({ setEntries }) {
  const [q, setQ] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [plan, setPlan] = React.useState(null);

  const run = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/journal/search", { query: q });
      setEntries(data.entries || []);
      setPlan(data.plan);
      toast.success(`Found ${data.matched} entries`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={run} className="card-sharp p-3 mb-4 flex items-center gap-2" data-testid="nl-search-bar">
      <Sparkles size={16} className="text-[var(--accent)] shrink-0"/>
      <input data-testid="nl-search-input" value={q} onChange={(e)=>setQ(e.target.value)}
        placeholder="Ask in plain English — e.g. 'biggest EURUSD losses this month tagged revenge'"
        className="input-dark flex-1 !py-2"/>
      <button data-testid="nl-search-run" disabled={busy || !q.trim()} type="submit"
        className="btn-primary flex items-center gap-2 !py-2">
        {busy ? <RefreshCw size={13} className="animate-spin"/> : <Search size={13}/>} Search
      </button>
      {plan && (
        <button type="button" onClick={()=>{setPlan(null); setQ("");}}
          data-testid="nl-search-clear"
          className="btn-outline !py-2 text-xs">Clear</button>
      )}
    </form>
  );
}
